"use client";

import {
  IMPROMPTU_LANGUAGES,
  IMPROMPTU_PROFILE_OPTIONS,
  type ImpromptuLanguageCode,
  type ImpromptuLearnerProfile,
} from "@/lib/impromptu/config";
import { IMPROMPTU_COPY } from "@/lib/impromptu/copy";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { useId } from "react";

type Surface = "light" | "dark";
type MenuKind = "language" | "level";

type Option<Value extends string> = {
  value: Value;
  label: string;
};

type SurfaceClasses = {
  label: string;
  trigger: string;
  chevron: string;
  menu: string;
  item: string;
  selectedItem: string;
};

const SURFACE_CLASSES: Record<Surface, SurfaceClasses> = {
  light: {
    label: "text-[#2C2C2E]",
    trigger:
      "rounded-xl border-2 border-[#E5E5EA] bg-white text-[#1C1C1E] hover:border-[#FD954F] focus-visible:border-[#C05C2F] focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    chevron: "text-[#8E8E93]",
    menu: "rounded-xl border-2 border-[#E5E5EA] bg-white text-[#1C1C1E] shadow-[0_20px_50px_rgba(44,44,46,0.16)]",
    item: "border-transparent text-[#2C2C2E] hover:border-[#FD954F]/30 hover:bg-[#FFF7F1] focus:border-[#FD954F] focus:bg-[#FFF2E8] focus:text-[#7A3018]",
    selectedItem: "border-[#FD954F] bg-[#FFF2E8] text-[#7A3018]",
  },
  dark: {
    label: "text-white/60",
    trigger:
      "rounded-lg border border-[#FD954F]/35 bg-white/10 text-white hover:border-[#FD954F]/60 hover:bg-white/[0.13] focus-visible:border-[#FD954F] focus-visible:ring-2 focus-visible:ring-[#FD954F]/70",
    chevron: "text-white/60",
    menu: "rounded-lg border border-white/10 text-white shadow-[0_20px_60px_rgba(0,0,0,0.55)]",
    item: "border-transparent text-white/75 hover:bg-white/[0.06] hover:text-white focus:border-orange-300/30 focus:bg-orange-500/15 focus:text-orange-100",
    selectedItem: "border-white bg-white text-black",
  },
};

const TRIGGER_BASE =
  "flex min-h-[3.25rem] w-full items-center justify-between gap-3 px-4 text-left text-sm font-bold outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-60";

const LABEL_BASE = "block text-xs font-bold uppercase tracking-[0.1em]";

type ImpromptuOptionMenuProps<Value extends string> = {
  label: string;
  value: Value;
  options: ReadonlyArray<Option<Value>>;
  onValueChange: (value: Value) => void;
  surface: Surface;
  kind: MenuKind;
  disabled: boolean;
};

function ImpromptuOptionMenu<Value extends string>({
  label,
  value,
  options,
  onValueChange,
  surface,
  kind,
  disabled,
}: ImpromptuOptionMenuProps<Value>) {
  const labelId = useId();
  const valueId = useId();
  const styles = SURFACE_CLASSES[surface];
  const selectedOption = options.find((option) => option.value === value) ?? options[0];
  const darkMenuSurface = kind === "language" ? "bg-black/90 backdrop-blur-2xl" : "bg-[#09090B]";

  return (
    <div>
      <span id={labelId} className={`${LABEL_BASE} ${styles.label}`}>
        {label}
      </span>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-labelledby={`${labelId} ${valueId}`}
            className={`${TRIGGER_BASE} ${styles.trigger} mt-2`}
            data-impromptu-settings-trigger={kind}
          >
            <span id={valueId} className="min-w-0 truncate">
              {selectedOption.label}
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 ${styles.chevron}`} aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={8}
            collisionPadding={16}
            data-impromptu-settings-menu={kind}
            data-surface={surface}
            className={`z-[10020] max-h-[min(24rem,calc(100dvh-2rem))] w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-2rem)] overflow-y-auto p-2 outline-none ${styles.menu} ${
              surface === "dark" ? darkMenuSurface : ""
            }`}
          >
            <DropdownMenu.Label className="sr-only">{label}</DropdownMenu.Label>
            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <DropdownMenu.Item
                  key={option.value}
                  onSelect={() => onValueChange(option.value)}
                  className={`flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 text-sm font-bold outline-none transition-colors ${
                    isSelected ? styles.selectedItem : styles.item
                  }`}
                >
                  <span>{option.label}</span>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4" aria-hidden="true" />}
                  </span>
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

export type ImpromptuSettingsProps = {
  languageCode: ImpromptuLanguageCode;
  learnerProfile: ImpromptuLearnerProfile;
  onLanguageChange: (language: ImpromptuLanguageCode) => void;
  onLearnerProfileChange: (profile: ImpromptuLearnerProfile) => void;
  surface?: Surface;
  disabled?: boolean;
  className?: string;
};

export function ImpromptuSettings({
  languageCode,
  learnerProfile,
  onLanguageChange,
  onLearnerProfileChange,
  surface = "light",
  disabled = false,
  className,
}: ImpromptuSettingsProps) {
  const languageOptions = IMPROMPTU_LANGUAGES.map((language) => ({
    value: language.code,
    label: language.name,
  }));

  return (
    <div className={className}>
      <div className="grid gap-4 sm:grid-cols-2">
        <ImpromptuOptionMenu
          label={IMPROMPTU_COPY.setup.languageLabel}
          value={languageCode}
          options={languageOptions}
          onValueChange={onLanguageChange}
          surface={surface}
          kind="language"
          disabled={disabled}
        />
        <ImpromptuOptionMenu
          label={IMPROMPTU_COPY.setup.levelLabel}
          value={learnerProfile}
          options={IMPROMPTU_PROFILE_OPTIONS}
          onValueChange={onLearnerProfileChange}
          surface={surface}
          kind="level"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
